import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiFetch } from '../lib/api'

interface User {
  id: string
  email: string
  name: string
  role: string
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<User | null>
  logout: () => void
  clearError: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    checkAuthStatus()
  }, [])

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        setIsLoading(false)
        return
      }

      const response = await apiFetch('/auth/validate')
      const data = await response.json()

      if (response.ok && data.user) {
        setUser({
          id: data.user.id,
          email: data.user.email,
          name: data.user.name || data.user.email,
          role: data.user.role,
        })
      } else {
        localStorage.removeItem('auth_token')
        setUser(null)
      }
    } catch {
      localStorage.removeItem('auth_token')
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string): Promise<User | null> => {
    try {
      setError(null)
      setIsLoading(true)

      const response = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      // Handle both response formats: { token, user } or { data: { access_token, user } }
      const token = data.token || data.data?.access_token
      const userData = data.user || data.data?.user

      if (response.ok && token && userData) {
        localStorage.setItem('auth_token', token)
        const loggedInUser: User = {
          id: userData.id,
          email: userData.email,
          name: userData.name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.email,
          role: userData.role,
        }
        setUser(loggedInUser)
        return loggedInUser
      } else {
        const errorMsg = data.error || data.message || 'Login failed'
        setError(errorMsg)
        return null
      }
    } catch {
      setError('Network error occurred')
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    apiFetch('/auth/logout', { method: 'POST' }).catch(console.error)
    localStorage.removeItem('auth_token')
    setUser(null)
    setError(null)
    window.location.href = '/login'
  }

  const clearError = () => {
    setError(null)
  }

  const value: AuthContextType = {
    user,
    isLoading,
    error,
    login,
    logout,
    clearError,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

